import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, Table } from 'semantic-ui-react';
import { getUserProfile, gridsMenuSelector, roleIdSelector, roleSelector } from '../../ducks/profile';
import { getLookupRequest, valuesListSelector, companyListSelector } from '../../ducks/lookup';
import './style.scss';
import InfiniteScrollTable from '../../components/InfiniteScrollTable';
import {
    clearFieldsSettings,
    editFieldsSettingRequest,
    editProgressSelector,
    fieldsSettingSelector,
    getFieldsSettingRequest,
    fieldsSettingCompaniesSelector,
    getFieldsSettingCompaniesRequest,
    progressSelector,
    toggleHidenStateRequest,
    editEntityNameRequest,
    editFieldsNameRequest,
} from '../../ducks/fieldsSetting';
import { ORDERS_GRID, RENTAL_GRID } from '../../constants/grids';
import Header from './components/header';
import TableBody from './components/table_body';
import TableHeader from './components/table_header';
import { sortFunc } from '../../utils/sort';

const List = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const containerRef = useRef(null);

    let [role, setRole] = useState(null);
    let [company, setCompany] = useState(null);

    const gridsList = useSelector(state => gridsMenuSelector(state)) || [];
    const rolesList = useSelector(state => companyListSelector(state, 'roles', company)) || [];
    const companiesList = useSelector(state => fieldsSettingCompaniesSelector(state)) || [];
    const settings = useSelector(state => fieldsSettingSelector(state)) || [];
    const loading = useSelector(state => progressSelector(state));
    const editProgress = useSelector(state => editProgressSelector(state)) || false;
    const userRole = useSelector(state => roleIdSelector(state));

    let [activeItem, setActiveItem] = useState((gridsList[0] || {}).name);

    const stateName = activeItem === RENTAL_GRID ? 'rentalState' : `${activeItem.slice(0, activeItem.length - 1)}State`;

    const statusList =
        useSelector(state =>
            valuesListSelector(state, stateName),
        ) || [];

    useEffect(() => {
        dispatch(
            getLookupRequest({
                name: 'roles',
                isForm: true,
            }),
        );

        dispatch(
            getFieldsSettingCompaniesRequest(),
        );
    }, [userRole]);

    useEffect(() => {
        activeItem && getStatus();
    }, [activeItem]);

    useEffect(() => {
        if (company && rolesList.length) {
            setRole(rolesList[0].value);
        } 
    }, [company]);

    useEffect(() => {
        if (company && !role && rolesList.length) {
            setRole(userRole);
        } 
    }, [company, rolesList]);

    useEffect(() => {
        if (!company && companiesList.length) {
            setCompany(companiesList[0].value);
        }
    }, [companiesList]);

    useEffect(() => {
        dispatch(clearFieldsSettings());
        activeItem && role && company && getSettings();
    }, [role, activeItem]);

    const getSettings = () => {
        dispatch(
            getFieldsSettingRequest({
                forEntity: activeItem,
                roleId: role,
                companyId: company,
            }),
        );
    };

    const reloadAppSettings = () => {
        dispatch(getUserProfile());
    };

    const getStatus = () => {
        dispatch(
            getLookupRequest({
                name: stateName,
                isForm: true,
                isState: true,
                params: {},
            }),
        );
    };

    const handleChangeActiveItem = useCallback((e, { value }) => {
        setActiveItem(value);
    }, []);

    const handleChangSettings = useCallback(
        (fieldName, accessType, state = null, extType) => {
            dispatch(
                editFieldsSettingRequest({
                    params: {
                        forEntity: activeItem,
                        roleId: role === 'null' ? undefined : role,
                        companyId: company === 'null' ? undefined : company,
                        fieldName,
                        accessType,
                        state,
                    },
                    extType,
                    callbackSuccess: () => {
                        getSettings();
                    },
                }),
            );
        },
        [activeItem, role, company],
    );

    const handleFieldRename = useCallback(
        (fieldName, value, extType) => {
            dispatch(
                editFieldsNameRequest({
                    params: {
                        forEntity: activeItem,
                        roleId: role === 'null' ? undefined : role,
                        companyId: company === 'null' ? undefined : company,
                        fieldName,
                        displayName: value
                    },
                    extType,
                    callbackSuccess: () => {
                        getSettings();
                    },
                }),
            );
        },
        [activeItem, role, company],
    );

    const handleToggleFieldFlags = useCallback(
        (fieldName, settingName, value, extType) => {
            dispatch(
                toggleHidenStateRequest({
                    params: {
                        forEntity: activeItem,
                        fieldName,
                        settingName,
                        roleId: role === 'null' ? undefined : role,
                        companyId: company === 'null' ? undefined : company,
                        value
                    },
                    extType,
                    callbackSuccess: getSettings,
                }),
            );
        },
        [activeItem, role, company],
    );

    const handleChangeRole = useCallback((e, { value }) => {
        setRole(value);
    }, []);

    const handleChangeCompany = useCallback((e, { value }) => {
        setCompany(value);
    }, []);

    const handleEntityRaname = (entity, name) => {
        dispatch(
            editEntityNameRequest({
                params: { 
                    entity, 
                    companyId: company,
                    name
                },
                callbackSuccess: reloadAppSettings,
            }),
        );
    };

    const { base: baseSettings = [], ext: extSettings = {} } = settings;

    return (
        <div className="container">
            <Loader active={loading && !baseSettings.length} size="huge" className="table-loader">
                Loading
            </Loader>
            <Header
                gridsList={gridsList}
                activeItem={activeItem}
                changeActiveItem={handleChangeActiveItem}
                rolesList={rolesList}
                companiesList={companiesList}
                role={role}
                company={company}
                changeRole={handleChangeRole}
                changeCompany={handleChangeCompany}
                onEntityRenamed={handleEntityRaname}
                t={t}
            />
            
                <div className={`scroll-table-container field-settings-table`} ref={containerRef}>
                    <InfiniteScrollTable
                        className="grid-table table-info"
                        onBottomVisible={() => {}}
                        structured
                        context={containerRef.current}
                        headerRow={<TableHeader statusList={statusList} t={t} />}
                    >
                        <Table.Body className="table-fields-setting">
                            {baseSettings.length ? (
                                <>
                                    <Table.Row>
                                        <Table.Cell className="field-settings-table_fixed">
                                            <div className="ui ribbon label">{t('Main fields')}</div>
                                        </Table.Cell>
                                        {statusList.map((state, i) => (
                                            <Table.Cell key={i} />
                                        ))}
                                    </Table.Row>
                                    {sortFunc(baseSettings, t, 'displayName').map(column => (
                                        <TableBody
                                            key={column.fieldName}
                                            column={column}
                                            statusList={statusList}
                                            editProgress={editProgress}
                                            changeSettings={handleChangSettings}
                                            renameField={handleFieldRename}
                                            toggleFieldFlags={handleToggleFieldFlags}
                                            t={t}
                                        />
                                    ))}
                                </>
                            ) : null}

                            {Object.entries(extSettings).map(([extType, extValues]) => (
                                <>
                                    <Table.Row>
                                        <Table.Cell className="field-settings-table_fixed">
                                            <div className="ui ribbon label">
                                                {t(extType)}
                                            </div>
                                        </Table.Cell>
                                        {statusList.map((state, i) => (
                                            <Table.Cell key={i} />
                                        ))}
                                    </Table.Row>
                                    {sortFunc(extValues, t, 'displayName').map(column => (
                                        <TableBody
                                            key={column.fieldName}
                                            column={column}
                                            statusList={statusList}
                                            changeSettings={handleChangSettings}
                                            renameField={handleFieldRename}
                                            editProgress={editProgress}
                                            toggleFieldFlags={handleToggleFieldFlags}
                                            t={t}
                                            extType={extType}
                                        />
                                    ))}
                                </>
                            ))}
                        </Table.Body>
                    </InfiniteScrollTable>
                </div>
               
        </div>
    );
};

export default List;
